import I18n from 'I18n';
import { AlignCenterIcon } from 'foundations-assets/icons/components/AlignCenterIcon';
import { AlignJustifyIcon } from 'foundations-assets/icons/components/AlignJustifyIcon';
import { AlignLeftIcon } from 'foundations-assets/icons/components/AlignLeftIcon';
import { AlignRightIcon } from 'foundations-assets/icons/components/AlignRightIcon';
import { BlockAlignmentBottomIcon } from 'foundations-assets/icons/components/BlockAlignmentBottomIcon';
import { BlockAlignmentCenterIcon } from 'foundations-assets/icons/components/BlockAlignmentCenterIcon';
import { BlockAlignmentLeftIcon } from 'foundations-assets/icons/components/BlockAlignmentLeftIcon';
import { BlockAlignmentMiddleIcon } from 'foundations-assets/icons/components/BlockAlignmentMiddleIcon';
import { BlockAlignmentRightIcon } from 'foundations-assets/icons/components/BlockAlignmentRightIcon';
import { BlockAlignmentTopIcon } from 'foundations-assets/icons/components/BlockAlignmentTopIcon';
import { ButtonGroup } from 'foundations-components/transitional/components/ButtonGroup';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useCallback, useMemo } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function hasAlignmentDirection(field) {
  if (typeof field !== 'object' || field === null) return false;
  if (!('alignment_direction' in field)) return true;
  const dir = field.alignment_direction;
  return dir === 'HORIZONTAL' || dir === 'VERTICAL' || dir === 'BOTH';
}
const ALIGNMENT_CONFIGS = {
  text: {
    values: ['LEFT', 'CENTER', 'RIGHT', 'JUSTIFY'],
    icons: {
      LEFT: AlignLeftIcon,
      CENTER: AlignCenterIcon,
      RIGHT: AlignRightIcon,
      JUSTIFY: AlignJustifyIcon
    },
    valueKey: 'text_align',
    i18nKeys: {
      LEFT: 'ContentComponents.cmv2.fields.textAlignment.direction.LEFT',
      CENTER: 'ContentComponents.cmv2.fields.textAlignment.direction.CENTER',
      RIGHT: 'ContentComponents.cmv2.fields.textAlignment.direction.RIGHT',
      JUSTIFY: 'ContentComponents.cmv2.fields.textAlignment.direction.JUSTIFY'
    }
  },
  horizontal: {
    values: ['LEFT', 'CENTER', 'RIGHT'],
    icons: {
      LEFT: BlockAlignmentLeftIcon,
      CENTER: BlockAlignmentCenterIcon,
      RIGHT: BlockAlignmentRightIcon
    },
    valueKey: 'horizontal_align',
    i18nKeys: {
      LEFT: 'ContentComponents.cmv2.fields.alignment.horizontal.direction.LEFT',
      CENTER: 'ContentComponents.cmv2.fields.alignment.horizontal.direction.CENTER',
      RIGHT: 'ContentComponents.cmv2.fields.alignment.horizontal.direction.RIGHT'
    }
  },
  vertical: {
    values: ['TOP', 'MIDDLE', 'BOTTOM'],
    icons: {
      TOP: BlockAlignmentTopIcon,
      MIDDLE: BlockAlignmentMiddleIcon,
      BOTTOM: BlockAlignmentBottomIcon
    },
    valueKey: 'vertical_align',
    i18nKeys: {
      TOP: 'ContentComponents.cmv2.fields.alignment.vertical.direction.TOP',
      MIDDLE: 'ContentComponents.cmv2.fields.alignment.vertical.direction.MIDDLE',
      BOTTOM: 'ContentComponents.cmv2.fields.alignment.vertical.direction.BOTTOM'
    }
  }
};
function extractValue(value, key) {
  if (typeof value === 'string') return value.toUpperCase();
  if (value && typeof value === 'object' && key) {
    const nested = value[key];
    if (typeof nested === 'string') return nested.toUpperCase();
  }
  return '';
}
function getAlignmentConfig(fieldType, alignmentDirection, fieldDefault) {
  const withDefault = key => Object.assign({}, ALIGNMENT_CONFIGS[key], {
    defaultValue: extractValue(fieldDefault, ALIGNMENT_CONFIGS[key].valueKey)
  });
  if (fieldType === 'textalignment') return [withDefault('text')];
  if (alignmentDirection === 'HORIZONTAL') return [withDefault('horizontal')];
  if (alignmentDirection === 'VERTICAL') return [withDefault('vertical')];
  return [withDefault('horizontal'), withDefault('vertical')];
}
export function AlignmentTool({
  info,
  onFieldChange
}) {
  const {
    field,
    path,
    currentValue
  } = info;
  const alignmentDirection = hasAlignmentDirection(field) ? field.alignment_direction : undefined;
  const configs = useMemo(() => getAlignmentConfig(field.type, alignmentDirection, field.default), [field.type, alignmentDirection, field.default]);
  const currentValues = useMemo(() => configs.reduce((acc, cfg) => {
    acc[cfg.valueKey] = extractValue(currentValue, cfg.valueKey) || cfg.defaultValue;
    return acc;
  }, {}), [configs, currentValue]);
  const handleSelect = useCallback((valueKey, newValue) => {
    const base = typeof currentValue === 'object' && currentValue !== null ? currentValue : {};
    onFieldChange(path, Object.assign({}, base, {
      [valueKey]: newValue
    }));
  }, [path, onFieldChange, currentValue]);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: configs.map(alignmentConfig => {
      const current = currentValues[alignmentConfig.valueKey];
      return /*#__PURE__*/_jsx(ButtonGroup, {
        children: alignmentConfig.values.map(alignment => {
          const Icon = alignmentConfig.icons[alignment];
          return /*#__PURE__*/_jsx(Tooltip, {
            title: I18n.text(alignmentConfig.i18nKeys[alignment]),
            children: /*#__PURE__*/_jsx(IconButton, {
              size: "small",
              use: "tertiary-light",
              active: current === alignment,
              onClick: () => handleSelect(alignmentConfig.valueKey, alignment),
              children: /*#__PURE__*/_jsx(Icon, {})
            })
          }, alignment);
        })
      }, alignmentConfig.valueKey);
    })
  });
}